import {ChevronDownIcon, DotFillIcon, MoonIcon, ShieldIcon, SyncIcon} from '@primer/octicons-react'
import {
  DeferredType,
  type CompletedReview,
  type PendingReviewRequest,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import styles from './Reviewers.module.css'
import {pullRequestsLayoutRoute} from '../../../routes/layout-route'
import {pullRequestsChangesWithRangeRoute} from '../../../routes/files-changed-routes'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {IconButton} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'
import {Suspense, useCallback, useState} from 'react'
import {UserSectionSkeleton} from '../UserSectionSkeleton'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Link} from '@github-ui/react-router'
import {ReviewerIdentity} from './ReviewerIdentity'
import {reviewState, reviewStateTooltip, deferredTooltip, codeOwnerTooltip} from './review-state-helpers'
import {useReRequestReviewMutation} from '../../../mutations/use-re-request-review-mutation'
import {useReviewers} from '../data/use-reviewers'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'

export function Reviewers() {
  return (
    <ErrorBoundary
      boundaryName="Reviewers"
      fallback={<span className={styles.emptyState}>Unable to load reviewers</span>}
    >
      <Suspense fallback={<UserSectionSkeleton placeholderRows={2} />}>
        <ReviewersComponent />
      </Suspense>
    </ErrorBoundary>
  )
}

export function ReviewersComponent() {
  const {data: reviewers} = useReviewers()

  const completedReviews = reviewers.completedReviews ?? []
  const pendingRequests = reviewers.pendingReviewRequests ?? []

  if (completedReviews.length === 0 && pendingRequests.length === 0) {
    return <span className={styles.emptyState}>No reviewers</span>
  }

  const writeAccessReviews = completedReviews.filter(r => r.hasWriteAccess || isCopilot(r.reviewer?.displayLogin ?? ''))
  const nonWriteAccessReviews = completedReviews.filter(
    r => !r.hasWriteAccess && !isCopilot(r.reviewer?.displayLogin ?? ''),
  )

  return (
    <div>
      <ul className={styles.reviewerList}>
        {writeAccessReviews.map(review => (
          <CompletedReviewRow
            key={`completed-${review.id}`}
            review={review}
            muted={isCopilot(review.reviewer?.displayLogin ?? '')}
          />
        ))}
        {pendingRequests.map(request => (
          <PendingReviewRow key={`pending-${request.reviewer?.id}`} request={request} />
        ))}
      </ul>
      {nonWriteAccessReviews.length > 0 && (
        <NonWriteAccessReviewers
          reviews={nonWriteAccessReviews}
          hasWriteAccessReviews={writeAccessReviews.length > 0}
        />
      )}
    </div>
  )
}

function NonWriteAccessReviewers({
  reviews,
  hasWriteAccessReviews,
}: {
  reviews: CompletedReview[]
  hasWriteAccessReviews: boolean
}) {
  const [isExpanded, setIsExpanded] = useState(true)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const toggle = useCallback(() => {
    sendPullRequestAnalyticsEvent('overview.reviewers_toggle', 'OVERVIEW_REVIEWER', {is_open: !isExpanded})
    setIsExpanded(prev => !prev)
  }, [isExpanded, sendPullRequestAnalyticsEvent])

  const count = reviews.length
  const label = `${hasWriteAccessReviews ? '+' : ''}${count} more ${count === 1 ? 'reviewer' : 'reviewers'}`

  return (
    <div className={styles.nonWriteAccessGroup}>
      <Tooltip text="Reviewers whose approvals may not affect merge requirements">
        <button type="button" className={styles.moreReviewersToggle} aria-expanded={isExpanded} onClick={toggle}>
          <span>{label}</span>
          <ChevronDownIcon
            size={16}
            className={clsx(styles.moreReviewersChevron, isExpanded && styles.moreReviewersChevronExpanded)}
          />
        </button>
      </Tooltip>
      {isExpanded && (
        <ul className={clsx(styles.reviewerList, 'pl-3')}>
          {reviews.map(review => (
            <CompletedReviewRow key={`completed-${review.id}`} review={review} muted />
          ))}
        </ul>
      )}
    </div>
  )
}

function CompletedReviewRow({review, muted = false}: {review: CompletedReview; muted?: boolean}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const {mutate: reRequestReview} = useReRequestReviewMutation()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // A schema-valid CompletedReview may omit the optional `reviewer` sub-message;
  // skip the row rather than render re-request/state controls with an empty login.
  const reviewer = review.reviewer
  if (!reviewer) return null

  const name = reviewer.displayLogin
  const {icon: stateIcon, className: stateClass} = reviewState(review.state)
  const changesWithRangePath = pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number,
    range: `BASE..${review.headSha}`,
  })

  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={reviewer} />
      <span className={styles.indicators}>
        {review.canReRequest && (
          <IconButton
            icon={SyncIcon}
            aria-label={`Re-request review from ${name}`}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
            onClick={() => {
              sendPullRequestAnalyticsEvent('overview.re_request_review', 'OVERVIEW_REVIEWER')
              reRequestReview({reviewerLogin: name})
            }}
          />
        )}
        <IconButton
          as={Link}
          to={changesWithRangePath}
          icon={stateIcon}
          aria-label={reviewStateTooltip(review.state, name)}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, muted ? 'color-fg-muted' : stateClass)}
        />
      </span>
    </li>
  )
}

function PendingReviewRow({request}: {request: PendingReviewRequest}) {
  // A schema-valid PendingReviewRequest may omit the optional `reviewer`
  // sub-message; skip the row rather than render controls with an empty login.
  const reviewer = request.reviewer
  if (!reviewer) return null

  const name = reviewer.displayLogin
  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={reviewer} />
      <span className={styles.indicators}>
        {request.deferredType !== DeferredType.UNSPECIFIED && (
          <IconButton
            icon={MoonIcon}
            aria-label={deferredTooltip(request.deferredType, name)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        {(request.isCodeOwner || request.isCodeOwnerFromDelegation) && (
          <IconButton
            icon={ShieldIcon}
            aria-label={codeOwnerTooltip(request)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        <IconButton
          icon={DotFillIcon}
          aria-label={`Awaiting requested review from ${name}`}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, 'color-fg-attention')}
        />
      </span>
    </li>
  )
}
